import React, { useState } from "react";
import { Plus, X } from "lucide-react";

const ORDER_FIELD_KEY = "order";
const ORDER_VALUE_PATTERN = /^\d+$/;
const isOrderValueValid = (value: string) =>
  value === "" || ORDER_VALUE_PATTERN.test(value);

interface EnhancedDynamicFieldsEditorProps {
  dynamicFields: Array<{ key: string; value: string }>;
  setDynamicFields: React.Dispatch<
    React.SetStateAction<Array<{ key: string; value: string }>>
  >;
  isReadOnly?: boolean;
}

export default function EnhancedDynamicFieldsEditor({
  dynamicFields,
  setDynamicFields,
  isReadOnly = false,
}: EnhancedDynamicFieldsEditorProps) {
  // Tracks the index of an order field whose latest keystroke was rejected
  // (non-numeric). Cleared as soon as a valid value is typed.
  const [orderTypingErrorIndex, setOrderTypingErrorIndex] = useState<number | null>(null);

  const handleDynamicKeyUpdate = (index: number, newKey: string) => {
    setDynamicFields((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        key: newKey,
      };
      return updated;
    });
  };

  const handleDynamicValueUpdate = (index: number, newValue: string) => {
    setDynamicFields((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        value: newValue,
      };
      return updated;
    });
  };

  const handleDynamicFieldRemove = (index: number) => {
    setDynamicFields((prev) => {
      const filtered = prev.filter((_, i) => i !== index);
      // Always ensure at least one empty row remains
      if (
        filtered.length === 0 ||
        filtered.every((field) => field.key.trim() !== "")
      ) {
        filtered.push({ key: "", value: "" });
      }
      return filtered;
    });
  };

  const handleAddDynamicField = () => {
    setDynamicFields((prev) => [...prev, { key: "", value: "" }]);
  };

  // Check if a key already exists (case-insensitive)
  const isKeyDuplicate = (key: string, currentIndex: number) => {
    const trimmedKey = key.trim().toLowerCase();
    return dynamicFields.some(
      (field, index) =>
        index !== currentIndex &&
        field.key.trim().toLowerCase() === trimmedKey &&
        field.key.trim() !== ""
    );
  };

  const formatFieldName = (name: string) => {
    return name
      .replace(/\./g, " ")
      .replace(/_/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const renderDynamicField = (
    field: { key: string; value: string },
    index: number
  ) => {
    // Check if this field has been "committed" (both key and value filled and add button clicked)
    const isCommittedField =
      field.key.trim() !== "" && index < dynamicFields.length - 1;

    if (isCommittedField) {
      const isOrderField = field.key.trim().toLowerCase() === ORDER_FIELD_KEY;

      if (isOrderField) {
        const hasInvalidStoredValue = !isOrderValueValid(field.value);
        const showOrderError =
          hasInvalidStoredValue || orderTypingErrorIndex === index;

        const handleOrderChange = (
          event: React.ChangeEvent<HTMLInputElement>
        ) => {
          const next = event.target.value;
          if (isOrderValueValid(next)) {
            handleDynamicValueUpdate(index, next);
            if (orderTypingErrorIndex === index) {
              setOrderTypingErrorIndex(null);
            }
          } else {
            setOrderTypingErrorIndex(index);
          }
        };

        return (
          <div key={`dynamic-committed-${index}`} className="">
            <label className="block text-text-primary text-sm font-medium mb-3">
              {formatFieldName(field.key)}
            </label>

            <div className="relative">
              <input
                type="text"
                inputMode="numeric"
                value={field.value}
                onChange={handleOrderChange}
                readOnly={isReadOnly}
                className={`w-full bg-surface-active border rounded-md px-4 py-3 pr-12 text-white placeholder:text-muted focus:outline-none focus:ring-2 focus:border-transparent ${
                  showOrderError
                    ? "border-red-500 focus:ring-red-500"
                    : "border-surface-border focus:ring-primary"
                }`}
                placeholder="Value"
              />
              {!isReadOnly && (
                <button
                  onClick={() => {
                    if (orderTypingErrorIndex === index) {
                      setOrderTypingErrorIndex(null);
                    }
                    handleDynamicFieldRemove(index);
                  }}
                  className="absolute top-3 right-3 text-text-secondary hover:text-error transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
            {showOrderError && (
              <p className="text-red-500 text-xs mt-2">
                Order must be a number
              </p>
            )}
          </div>
        );
      }

      // Show as standard field format for committed fields
      return (
        <div key={`dynamic-committed-${index}`} className="">
          <label className="block text-text-primary text-sm font-medium mb-3">
            {formatFieldName(field.key)}
          </label>

          <div className="relative">
            <textarea
              value={field.value}
              onChange={(e) => handleDynamicValueUpdate(index, e.target.value)}
              readOnly={isReadOnly}
              className="w-full bg-surface-active border border-surface-border rounded-md px-4 py-3 pr-12 text-white placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-vertical"
              placeholder="Value"
            />
            {!isReadOnly && (
              <button
                onClick={() => handleDynamicFieldRemove(index)}
                className="absolute top-3 right-3 text-text-secondary hover:text-error transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>
      );
    } else {
      // Always show input format for the last row (new property input).
      // Hide the "Add Property" affordance entirely for read-only viewers —
      // there is nothing they can add.
      if (isReadOnly) {
        return null;
      }
      const isDuplicate = isKeyDuplicate(field.key, index);
      const isNewOrderField =
        field.key.trim().toLowerCase() === ORDER_FIELD_KEY;
      const newOrderValueInvalid =
        isNewOrderField && !isOrderValueValid(field.value);

      return (
        <div key={`add-property-${index}`}>
          <div className="mb-3">
            <label className="block text-text-primary text-sm font-medium mb-3">
              Add Property
            </label>

            <div className="flex gap-3 mb-4">
              <input
                type="text"
                value={field.key}
                onChange={(e) => handleDynamicKeyUpdate(index, e.target.value)}
                placeholder="Property Name"
                className={`flex-1 bg-surface-active border border-surface-border rounded-md px-3 py-1 h-9 text-white placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                  isDuplicate ? "border-red-500 focus:ring-red-500" : ""
                }`}
              />
              <button
                onClick={handleAddDynamicField}
                className="bg-primary hover:bg-primary-dark text-button-text rounded-md px-4 h-9 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={
                  !field.key.trim() ||
                  !field.value.trim() ||
                  isDuplicate ||
                  newOrderValueInvalid
                }
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>

            {isDuplicate && (
              <p className="text-red-500 text-xs mb-3">Key already exists</p>
            )}

            <textarea
              value={field.value}
              onChange={(e) => handleDynamicValueUpdate(index, e.target.value)}
              placeholder={
                isNewOrderField
                  ? "Value (numbers only)"
                  : "Value (enter JSON objects/arrays as needed)"
              }
              className={`w-full bg-surface-active border rounded-md px-4 py-3 text-white placeholder:text-muted focus:outline-none focus:ring-2 focus:border-transparent resize-vertical min-h-[100px] ${
                newOrderValueInvalid
                  ? "border-red-500 focus:ring-red-500"
                  : "border-surface-border focus:ring-primary"
              }`}
            />
            {newOrderValueInvalid && (
              <p className="text-red-500 text-xs mt-2">
                Order must be a number
              </p>
            )}
          </div>
        </div>
      );
    }
  };

  return (
    <>{dynamicFields.map((field, index) => renderDynamicField(field, index))}</>
  );
}
